import { HttpParams } from '@angular/common/http'
import { DisplayDateFormat, formatDate, ISODateFormat } from '@northfork/basics'
import { Daterange } from '@northfork/ng-basics'

/**
 * @internal
 * Gets the default date range of the distribution panel,
 * which starts 1.5 years before the current date and ends 1 year after the current date.
 *
 * @returns default date range for the distributions.
 */
export function getDefaultDistributionsDaterange(
  windowDate: Date | null,
): Daterange {
  return {
    beginDate: getDefaultDistributionsBeginDate(windowDate),
    endDate: getDefaultDistributionsEndDate(),
  }
}

/**
 * Gets the begin date for the distribution panel, which is 1.5 years before the session start date.
 *
 * @returns begin date for the distributions, as an ISO8601 compliant date string.
 */
function getDefaultDistributionsBeginDate(
  windowDate: Date | null,
): string | null {
  const beginDate = new Date(
    windowDate ? windowDate.getTime() : new Date().getTime(),
  )
  beginDate?.setFullYear(beginDate.getFullYear() - 1)
  beginDate?.setMonth(beginDate.getMonth() - 6)
  return formatDate(beginDate, ISODateFormat)
}

/**
 * Gets the default end date for the distribution panel, which is 1 year after the current date.
 *
 * @returns default end date for the distributions, as an ISO8601 compliant date string.
 */
function getDefaultDistributionsEndDate(): string | null {
  const endDate = new Date()
  endDate.setFullYear(endDate.getFullYear() + 1)
  return formatDate(endDate, ISODateFormat)
}

/**
 * Convert a date range to http query parameters. If the date range is null, the query parameters will be empty.
 * Otherwise, the query parameters will be the start and end dates of the date range. Null dates will be omitted.
 * Date strings must be ISO8601 compliant (YYYY-MM-DD).
 *
 * @param dateRange The date range to convert.
 * @returns An HttpParams object with the given date range
 */
export function getDateRangeAsHttpParams(
  dateRange?: Daterange | null,
): HttpParams {
  let params: HttpParams = new HttpParams()
  if (dateRange?.beginDate) {
    // api uses startDate, while northfork uses beginDate
    params = params.set('startDate', dateRange.beginDate)
  }
  if (dateRange?.endDate) {
    params = params.set('endDate', dateRange.endDate)
  }
  return params
}

/**
 * Formats a date range to a display string.
 *
 * @param daterange The date range to display
 * @returns date range display string
 */
export function displayDaterange(daterange: Daterange): string {
  if (!daterange.beginDate && !daterange.endDate) {
    return 'All of time'
  }

  const beingStr = displayBeginDate(daterange)
  const endStr = displayEndDate(daterange)
  return `${beingStr} - ${endStr}`
}

/**
 * Formats the begin date of a date range to a display string.
 */
function displayBeginDate(daterange: Daterange): string {
  if (!daterange.beginDate) {
    return 'Beginning of time'
  }
  const str = formatDate(daterange.beginDate, DisplayDateFormat)
  if (!str) {
    throw new Error(`Invalid begin date: ${daterange.beginDate}`)
  }
  return str
}

/**
 * Formats the end date of a date range to a display string.
 */
function displayEndDate(daterange: Daterange): string {
  if (!daterange.endDate) {
    return 'End of time'
  }
  const str = formatDate(daterange.endDate, DisplayDateFormat)
  if (!str) {
    throw new Error(`Invalid end date: ${daterange.endDate}`)
  }
  return str
}
